import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCheckCircle, faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import hubIcon from "../assets/hub-1.svg";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../redux/slices/authSlice";
import { getTranslation } from "../translations/getTranslations";
import ForgotPasswordModal from "./modals/ForgotPasswordModal";
import { useNotification } from "../notifications/Notifications";
import { userLogin, userRegister } from "../services/authService";

const Auth = () => {
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [validEmail, setValidEmail] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const language = useSelector(state => state.settings.language);
  const [validationStatus, setValidationStatus] = useState({
    hasLower: false,
    hasUpper: false,
    hasNumeric: false,
    hasSpecialChar: false,
    isMinLength: false,
  });

  const dispatch = useDispatch();
  const notify = useNotification();
  const translation = useCallback(key => getTranslation("auth", key, language), [language]);

  useEffect(() => {
    if (loginAttempts >= 3) {
      setIsDisabled(true);
      setErrorMessage(translation("_errorAttempts"));

      const timer = setTimeout(() => {
        setLoginAttempts(0);
        setIsDisabled(false);
        setErrorMessage("");
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [loginAttempts, translation]);

  useEffect(() => {
    setFullName("");
    setPassword("");
    setEmail("");
    setConfirmPassword("");
    setErrorMessage("");
  }, [isSignUp]);

  const checkPasswordStrength = password => {
    setValidationStatus({
      hasLower: /[a-z]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasNumeric: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      isMinLength: password.length >= 8,
    });
  };

  const handleNewPasswordChange = e => {
    const password = e.target.value;
    setPassword(password);
    checkPasswordStrength(password);
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setErrorMessage(translation("_requiredSignInFields"));
      return;
    }

    try {
      const data = await userLogin(email, password);

      dispatch(
        login({
          fullName: data.fullName,
          userId: data.userId,
          userType: data.userType,
          token: data.token,
          refreshToken: data.refreshToken,
        }),
      );

      setLoginAttempts(0);
    } catch (error) {
      setLoginAttempts(prev => prev + 1);
      setErrorMessage(error.message);
    }
  };

  const handleSignUp = async () => {
    if (!fullName || !email || !password || !confirmPassword) {
      setErrorMessage(translation("_requiredSignUpFields"));
      return;
    }

    if (!validEmail) {
      setErrorMessage(translation("_invalidEmail"));
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage(translation("_passwordMismatch"));
      return;
    }

    try {
      await userRegister(fullName, email, password);
      notify("success", translation("_verificationEmail"));

      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setIsSignUp(false);
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const handleEmailChange = text => {
    if (isSignUp) {
      const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
      if (!emailPattern.test(text)) {
        setValidEmail(false);
      } else {
        setValidEmail(true);
      }
    }

    setEmail(text);
  };

  const handleSubmit = () => {
    setErrorMessage("");

    if (isSignUp) {
      handleSignUp();
    } else {
      handleLogin();
    }
  };

  useEffect(() => {
    const handleKeyDown = e => {
      if (
        e.key === "Enter" &&
        (document.activeElement.tagName === "INPUT" || document.activeElement.tagName === "BUTTON")
      ) {
        handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  return (
    <div className={`absolute inset-0 z-[1000] flex items-center justify-center bg-overlayColor`}>
      <div
        className={`${isSignUp ? "w-[450px]" : "w-[400px]"} rounded bg-backgroundPrimary p-5 text-center text-lg text-textPrimary shadow-lg`}
      >
        <div className="flex items-center justify-center py-5">
          <span className={`bg-clip-text text-2xl font-bold uppercase`}>Olympia</span>
          <img src={hubIcon} alt="Hub Icon" className="mx-1 h-10 w-10" />
          <span className={`ml-1 bg-clip-text text-2xl font-bold uppercase`}>Gateway</span>
        </div>
        <div className="px-5">
          {isSignUp && (
            <input
              className={`mb-3 w-full border-b border-loginBorder bg-transparent p-3 outline-none`}
              type="text"
              placeholder={translation("_fullNamePlaceholder")}
              value={fullName}
              onChange={e => setFullName(e.target.value)}
            />
          )}
          <div className="relative mb-3">
            <input
              className={`w-full border-b border-loginBorder bg-transparent p-3 outline-none`}
              type="text"
              placeholder={translation("_emailPlaceholder")}
              value={email}
              onChange={e => handleEmailChange(e.target.value)}
            />
            {isSignUp && (
              <div className={`absolute inset-y-0 right-0 flex items-center pr-3 text-textPrimary`}>
                <FontAwesomeIcon icon={email === "" ? faCircle : validEmail ? faCircleCheck : faCircleXmark} />
              </div>
            )}
          </div>
          <div className="relative mb-3">
            <input
              className={`w-full border-b border-loginBorder bg-transparent p-3 pr-12 outline-none`}
              type={showPassword ? "text" : "password"}
              placeholder={translation("_passwordPlaceholder")}
              value={password}
              onChange={handleNewPasswordChange}
            />
            <button
              type="button"
              className={`absolute inset-y-0 right-0 flex items-center pr-3 text-textPrimary`}
              onClick={() => setShowPassword(prev => !prev)}
            >
              <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
            </button>
          </div>

          {isSignUp && (
            <>
              <div className="relative mb-3">
                <input
                  className={`w-full border-b border-loginBorder bg-transparent p-3 pr-12 text-textPrimary outline-none`}
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={translation("_confirmPasswordPlaceholder")}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  className={`absolute inset-y-0 right-0 flex items-center pr-3 text-textPrimary`}
                  onClick={() => setShowConfirmPassword(prev => !prev)}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEye : faEyeSlash} />
                </button>
              </div>
              <div className="mt-4 flex justify-center">
                <ul className="w-[90%] space-y-1 text-left text-xs">
                  <li
                    className={`flex items-center ${validationStatus.hasLower ? "text-green-900" : "text-textPrimary"}`}
                  >
                    {translation("_passwordValidationDesc")}
                  </li>
                  <li
                    className={`flex items-center ${validationStatus.hasLower ? "text-green-900" : "text-textPrimary"}`}
                  >
                    <FontAwesomeIcon icon={validationStatus.hasLower ? faCheckCircle : faCircle} className="mr-2" />
                    {translation("_passwordValidationLowLetter")}
                  </li>
                  <li
                    className={`flex items-center ${validationStatus.hasUpper ? "text-green-900" : "text-textPrimary"}`}
                  >
                    <FontAwesomeIcon icon={validationStatus.hasUpper ? faCheckCircle : faCircle} className="mr-2" />
                    {translation("_passwordValidationUpperLetter")}
                  </li>
                  <li
                    className={`flex items-center ${validationStatus.hasNumeric ? "text-green-900" : "text-textPrimary"}`}
                  >
                    <FontAwesomeIcon icon={validationStatus.hasNumeric ? faCheckCircle : faCircle} className="mr-2" />
                    {translation("_passwordValidationNumeric")}
                  </li>
                  <li
                    className={`flex items-center ${validationStatus.hasSpecialChar ? "text-green-900" : "text-textPrimary"}`}
                  >
                    <FontAwesomeIcon
                      icon={validationStatus.hasSpecialChar ? faCheckCircle : faCircle}
                      className="mr-2"
                    />
                    {translation("_passwordValidationSpecialChar")}
                  </li>
                  <li
                    className={`flex items-center ${validationStatus.isMinLength ? "text-green-900" : "text-textPrimary"}`}
                  >
                    <FontAwesomeIcon icon={validationStatus.isMinLength ? faCheckCircle : faCircle} className="mr-2" />
                    {translation("_passwordValidationLength")}
                  </li>
                </ul>
              </div>
            </>
          )}

          <p className="my-3 h-10 text-sm text-red-500">{errorMessage}</p>

          <div className="pb-5">
            {!isSignUp && (
              <button
                className={`mb-3 text-base text-textPrimary hover:underline`}
                onClick={() => setShowForgotPasswordModal(true)}
              >
                {translation("_forgotPassword")}
              </button>
            )}
            <button
              className={`w-full cursor-pointer rounded p-3 text-textSecondary ${
                isDisabled ? `bg-loginDisabledButton disabled:cursor-not-allowed` : `bg-buttonSubmit text-buttonText`
              }`}
              onClick={handleSubmit}
              disabled={isDisabled}
            >
              {isSignUp ? translation("_signUp") : translation("_signIn")}
            </button>

            <button
              className={`mt-3 text-base text-textPrimary hover:underline`}
              onClick={() => setIsSignUp(prev => !prev)}
            >
              {isSignUp ? translation("_switchToSignIn") : translation("_switchToSignUp")}
            </button>
          </div>
        </div>
      </div>

      <ForgotPasswordModal showModal={showForgotPasswordModal} onClose={() => setShowForgotPasswordModal(false)} />
    </div>
  );
};

export default Auth;
